import { Radio } from "../ui/inputs/Radio";
import PersonneMoral from "./PersonneMoral";
import { useCrmStore } from "../../stores/crm";
import PersonnePhysique from "./PersonnePhysique";
import { Input } from "../ui/inputs/Input";
import { TextArea } from "../ui/inputs/TextArea";
import InterlocuteursSection from "./InterlocuteursSection";
import { useButtonStore } from "../../stores/components/button";
import { useEffect, useState } from "react";
import {
  createCustomerFile,
  getOneCustomerFile,
  updateCustomerFile,
} from "../../api/customerFile";
import { showToastError, showToastSuccess } from "../../services/toast";
import { getJuridicForms } from "../../api/juridicforms";
import { fieldsRadioActif, fieldsRadioPersonne, pays } from "../../data/crm";
import { SelectText } from "../ui/inputs/SelectText";
import useModals from "../../hooks/useModals";
import { ActionButton, DetailHeader } from "../ui/page/PageShell";

const countryOptions = pays.map((country) => ({
  label: country.title,
  value: country.iso2,
}));

const ClientFolder: React.FC = () => {
  //Store
  const {
    formData,
    setFormData,
    billingContacts,
    setJuridicsForm,
    mandate,
    setMandate,
    viewOrUpdate,
    idCustomer,
    setViewOrUpdate,
    setBillingContacts,
    resetCrm,
    updateCrm,
  } = useCrmStore();
  const { setCreateButton } = useButtonStore();

  //Hooks
  const { modals, openModals, openModal, closeModal } = useModals();

  // State
  const [commanditaire, setCommanditaire] = useState<boolean>(false);
  const [personn, setPersonn] = useState<string>("moral");
  const [exonerated, setExonerated] = useState<boolean>(false);
  const [followUp, setFollowUp] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Récupère les données du dossier client si on est en mode modification ou visualisation
  useEffect(() => {
    if (viewOrUpdate !== "") {
      getOneCustomerFile(idCustomer)
        .then((res) => {
          if (res?.data?.name === null || res?.data?.name === "") {
            setPersonn("physique");
          }
          setFormData(res?.data);

          setMandate({
            active: res?.data?.mandate?.active,
            RUM: res?.data?.mandate?.RUM,
            dateSignature: res?.data?.mandate?.dateSignature,
          });

          if (res?.data?.entityName !== "") {
            setCommanditaire(true);
          }
          setBillingContacts(res?.data?.billingContacts);
          setExonerated(res?.data?.exonerated);
          setFollowUp(res?.data?.followUp);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, [viewOrUpdate, updateCrm]);

  // Récupère les formes juridiques pour les personnes morales
  useEffect(() => {
    if (personn === "moral") {
      getJuridicForms()
        .then((res) => {
          // Formate les données pour les rendre utilisables dans le composant Select
          const formattedData = res?.data?.map((data: string) => ({
            label: data,
            value: data,
          }));
          setJuridicsForm(formattedData);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
        });
    }
  }, [personn]);

  // Fonctions permettant de changer les valeurs des inputs
  const handleChangePersonn = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersonn(e.target.value);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleSelectTextChange = (selectedOption: any) => {
    if (selectedOption) {
      setFormData({
        ...formData,
        countryiso2: selectedOption.value,
        country: selectedOption.label,
      });
    } else {
      setFormData({ ...formData, countryiso2: null, country: null });
    }
  };
  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Fonction handleChange pour gérer l'état de `mandate.active`
  const handleChangeMandate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMandate({
      ...mandate,
      [e.target.name]:
        e.target.name === "active"
          ? e.target.value === "true"
          : e.target.name === "dateSignature"
          ? new Date(e.target.value) // Convertir la date string en objet Date
          : e.target.value,
    });
  };

  // Fonction permettant de soumettre le formulaire de création de dossier client
  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Permet de ne pas envoyer de données vides
    const contacts = billingContacts.filter(
      (contact) => contact.email !== "" && contact.email !== undefined
    );

    const data = {
      ...formData,
      billingContacts: contacts,
      mandate: mandate,
      exonerated: exonerated,
      followUp: followUp,
      name: personn === "moral" ? formData.name.toUpperCase() : "",
    };
    {
      viewOrUpdate === ""
        ? createCustomerFile(data)
            .then(() => {
              setCreateButton(false);
              setFormData({});
              showToastSuccess("Dossier client créé avec succès");
              resetCrm();
            })
            .catch(() => {
              showToastError("Erreur lors de la création du dossier client");
            })
            .finally(() => {
              setIsLoading(false);
            })
        : updateCustomerFile(idCustomer, data)
            .then(() => {
              setCreateButton(false);
              setFormData({});
              showToastSuccess("Dossier client modifié avec succès");
              resetCrm();
            })
            .catch(() => {
              showToastError(
                "Erreur lors de la modification du dossier client"
              );
            })
            .finally(() => {
              setIsLoading(false);
            });
    }
  };
  
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      <DetailHeader
        title={
          viewOrUpdate !== ""
            ? formData.name === null || formData.name === ""
              ? `${formData.firstName} ${formData.lastName}`
              : formData.name
            : "Création d'un dossier client"
        }
        hint="Merci de renseigner les informations du dossier"
        onClose={() => {
          setCreateButton(false);
          setViewOrUpdate("");
          setFormData({});
          resetCrm();
        }}
        actions={
          <ActionButton
            variant="primary"
            type="submit"
            form="clientForm"
            disabled={isLoading}
            icon={
              isLoading ? (
                <span
                  aria-hidden="true"
                  className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                />
              ) : undefined
            }
          >
            {isLoading
              ? viewOrUpdate !== ""
                ? "Modification..."
                : "Création..."
              : viewOrUpdate !== ""
                ? "Modifier"
                : "Créer"}
          </ActionButton>
        }
      />

      <div className="flex min-h-0 w-full flex-1 items-start gap-3 overflow-hidden p-3 sm:p-4">
        <form
          className="w-full h-full min-h-0 flex flex-col xl:flex-row gap-2 xl:gap-3 overflow-hidden"
          onSubmit={handleSubmitCreate}
          id="clientForm"
        >
          <div className="flex h-full min-h-0 w-full flex-col overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200 bg-white xl:w-2/3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
              <h4 className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                Renseignements du dossier
              </h4>
              <ActionButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openModal(
                    "interlocuteurs2",
                    <InterlocuteursSection
                      closeModal={() => closeModal("interlocuteurs2")}
                    />
                  );
                }}
              >
                Gestion espace client
              </ActionButton>
            </div>
            <div className="m-4 flex flex-col justify-start items-start rounded-xl border border-slate-200 p-3">
            <div className="flex justify-start items-start w-full mb-4 pl-4">
              {/*Permet de selectionner personne moral ou physique */}
              {fieldsRadioPersonne?.map((field) => (
                <Radio
                label={field.label}
                name={field.name}
                id={field.id}
                value={field.id}
                key={field.id}
                checked={personn === field.id}
                onChange={handleChangePersonn}
                />
              ))}
            </div>
              <div className="flex justify-start items-start pl-4 gap-5 w-full">
                <div className="flex justify-start items-center text-grey w-full">
                  <label htmlFor="" className="font-thin text-md">
                    Commanditaire:
                  </label>
                  <input
                    type="checkbox"
                    name="Commanditaire"
                    id="Commanditaire"
                    onChange={() => setCommanditaire(!commanditaire)}
                    className="mt-1 ml-4 cursor-pointer font-thin"
                    />
                </div>
                <div className="flex items-center text-grey w-full ">
                  <label htmlFor="" className="font-thin text-md">
                    Exonéré TVA:
                  </label>
                  <input
                    type="checkbox"
                    name="exonerated"
                    checked={exonerated}
                    id="exonerated"
                    onChange={() => setExonerated(!exonerated)}
                    className="mt-1 ml-4 cursor-pointer"
                    />
                </div>
                <div className="flex items-center text-grey w-full ">
                  <label htmlFor="" className="font-thin text-md">
                    Relance activé:
                  </label>
                  <input
                    type="checkbox"
                    name="followUp"
                    checked={followUp}
                    id="followUp"
                    onChange={() => setFollowUp(!followUp)}
                    className="mt-1 ml-4 cursor-pointer"
                    />
                </div>
            </div>
            </div>
            <div className="w-[97%] m-auto h-full 3xl:p-4 py-2 px-4">
              {/* Affiche les inputs en fonction de la personne selectionnée */}
              {personn === "moral" ? <PersonneMoral /> : <PersonnePhysique />}

              {commanditaire ? (
                <Input
                label="Dénomination de facturation"
                name="entityName"
                value={formData.entityName || ""}
                onChange={handleChange}
                clasName="w-1/2 3xl:mt-5 mt-2"
                />
              ) : null}

              {/* Adresse */}
              <TextArea
                label="Adresse"
                name="address"
                required={true}
                value={formData.address || ""}
                onChange={handleChangeTextArea}
                clasName="w-1/2 3xl:mt-5 mt-2"
                />
              
              <div className="flex 3xl:mt-5 mt-2">
                <Input
                  label="CP"
                  name="postalCode"
                  id="postalCode"
                  required={true}
                  value={formData.postalCode || ""}
                  onChange={handleChange}
                  clasName="w-1/5 mr-5"
                  />
                <Input
                  label="Ville"
                  name="city"
                  required={true}
                  value={formData.city || ""}
                  onChange={handleChange}
                  clasName="w-2/5 mr-5"
                  />
                <SelectText
                  fetchOptions={countryOptions}
                  label="Pays"
                  name="countryiso2"
                  value={formData.countryiso2 || ""}
                  onChange={handleSelectTextChange}
                  className="w-2/5"
                  />
              </div>

              {/* Tel */}
              <div className="flex 3xl:mt-5 mt-2">
                <Input
                  label="Téléphone"
                  name="phoneNumber"
                  value={formData.phoneNumber || ""}
                  onChange={handleChange}
                  clasName="w-1/3 mr-5"
                  />
                <Input
                  label="Numéro TVA"
                  name="vatNumber"
                  value={formData.vatNumber || ""}
                  onChange={handleChange}
                  clasName="w-1/3 "
                  />
              </div>

              <div className="flex justify-between items-center">
                {/* Descriptif */}
                <TextArea
                  label="Descriptif d'activité"
                  name="activityDescription"
                  value={formData.activityDescription || ""}
                  onChange={handleChangeTextArea}
                  clasName="w-1/2 3xl:mt-5 mt-2 min-h-[100px]"
                />
              </div>
            </div>
          </div>
          <div className="h-full min-h-0 w-full overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200 bg-white p-4 xl:w-1/3">
            <h4 className="mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Informations bancaires
            </h4>
            <Input
              label="Banque "
              name="bankName"
              value={formData.bankName || ""}
              onChange={handleChange}
              clasName="w-3/4 3xl:mb-4 mb-2"
            />
            <TextArea
              label="Adresse banque"
              name="bankAddress"
              value={formData.bankAddress || ""}
              onChange={handleChangeTextArea}
              clasName="w-3/4 3xl:mb-4 mb-2"
            />
            <Input
              label="IBAN"
              name="iban"
              value={formData.iban || ""}
              onChange={handleChange}
              clasName="w-3/4 3xl:mb-4 mb-2"
            />
            <Input
              label="BIC"
              name="bic"
              value={formData.bic || ""}
              onChange={handleChange}
              clasName="w-3/4 3xl:mb-4 mb-2"
            />
            <h4 className="mt-6 mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Mandat
            </h4>
            <div className="flex items-center pb-4">
              {/* Permet d'activer ou desactiver le */}
              {fieldsRadioActif?.map((field) => (
                <Radio
                  key={field.id}
                  label={field.label}
                  name={field.name}
                  id={field.id}
                  value={field.value}
                  checked={mandate.active == field.value}
                  onChange={handleChangeMandate}
                />
              ))}
            </div>
            <Input
              label="RUM"
              name="RUM"
              value={mandate.RUM || ""}
              onChange={handleChangeMandate}
              clasName="w-3/4 3xl:mb-4 mb-2"
            />
            <Input
              label="Date"
              name="dateSignature"
              type="date"
              value={
                mandate.dateSignature
                  ? new Date(mandate.dateSignature).toISOString().split("T")[0]
                  : ""
              }
              onChange={handleChangeMandate}
              clasName="w-3/4 3xl:mb-4 mb-2"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientFolder;
